<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { CheckCircle2 } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { AdvancedAnalysisSchema } from '../models/AdvancedAnalysisSchema'
  import { AdvancedAnalysisJsonEditorSchema } from '../services/AdvancedAnalysisJsonConfigurationService'
  import { AdvancedAnalysisService } from '../services/AdvancedAnalysisService'
  import { AdvancedAnalysisTypeGuardService } from '../services/AdvancedAnalysisTypeGuardService'

  interface IJsonAdvancedAnalysisConfigurationProps {
    advancedAnalysisSchemas: Array<AdvancedAnalysisSchema>
    modelValue: Record<string, any>
    placeHolder?: string
    configurationLevel: number
    editorSchema: AdvancedAnalysisJsonEditorSchema
    allowTypeChange: boolean
  }

  interface IJsonAnalysisConfigConfigurationEmits {
    (e: 'update:modelValue', value: Record<string, any>): void
    (e: 'advanced-analysis-type-changed', type: string, configuration: Record<string, any>): void
  }

  const props = defineProps<IJsonAdvancedAnalysisConfigurationProps>()
  const emit = defineEmits<IJsonAnalysisConfigConfigurationEmits>()

  const modelValue = ref<Record<string, any>>({ ...(props.modelValue ?? {}) })

  watch(
    modelValue,
    (newVal) => {
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  const newApplyableAdvancedAnalysisType = ref<string | null>(null)

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        schema: props.editorSchema,
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (props.allowTypeChange === true) {
              onEditorChangeForAdvancedAnalysis(newVal)
            } else {
              onEditorChangeForField(newVal)
            }
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
    }
  }

  const onEditorChangeForAdvancedAnalysis = (editorValue: any) => {
    newApplyableAdvancedAnalysisType.value = null
    if (typeof editorValue === 'object' && editorValue != null) {
      // AdvancedAnalysis uses _type property
      if (editorValue._type != null) {
        const cfgType = AdvancedAnalysisService.tryGetAdvancedAnalysisSchemaByType(
          editorValue._type,
          props.advancedAnalysisSchemas
        )
        if (
          cfgType != null &&
          cfgType.type !== AdvancedAnalysisService.CUSTOM_ADVANCED_ANALYSIS_TYPE &&
          (props.configurationLevel === 1 ||
            !AdvancedAnalysisTypeGuardService.supportsChildAdvancedAnalyses(cfgType))
        ) {
          newApplyableAdvancedAnalysisType.value = cfgType.type
        }
      }
      modelValue.value = editorValue
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    modelValue.value = editorValue
  }

  const setJSONEditorModel = (value: Record<string, any>) => {
    if (editor != null) {
      newApplyableAdvancedAnalysisType.value = null
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const onApplyNewAdvancedAnalysisType = () => {
    if (newApplyableAdvancedAnalysisType.value != null) {
      emit(
        'advanced-analysis-type-changed',
        newApplyableAdvancedAnalysisType.value as string,
        modelValue.value
      )
    }
  }

  onMounted(() => {
    initializeJSONEditor()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.allowTypeChange === true" class="mb-3 flex justify-end">
      <a-tooltip>
        <template #title>
          {{ `Apply Advanced Analysis type: ${newApplyableAdvancedAnalysisType}` }}
        </template>
        <Button
          type="primary"
          :disabled="newApplyableAdvancedAnalysisType == null"
          @click="onApplyNewAdvancedAnalysisType"
        >
          <span class="ant-button-flex-content">
            <CheckCircle2 class="h-4 w-4" />
            {{ 'Apply Advanced Analysis Type' }}
          </span>
        </Button>
      </a-tooltip>
    </div>
    <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
  </div>
</template>
